import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ApiKeyCredential } from '../../models/appcloud/api-key-credential.model';
import { ErrorHandlingService } from '../error-handling';
import { HttpHeaderConstants } from '../http-header.constant';
import { SpinnerService } from '../spinner';

@Injectable({
    providedIn: 'root',
})
export class ApiKeyCredentialService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private API_KEY_CREDENTIAL_URL: string = environment.publicApiHost + '/appcloud/api-key-credentials';

    create(apiKeyCredential: ApiKeyCredential, additionalHeaders: any = {}): Observable<ApiKeyCredential> {
        this.#spinnerService.addProcess('ApiKeyCredentialService.create', 'Creating your api key credential...');

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.post<ApiKeyCredential>(this.API_KEY_CREDENTIAL_URL, apiKeyCredential, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while saving your credential. Please try again later.'
                );
                return of<ApiKeyCredential>();
            }),
            finalize(() => {
                this.#spinnerService.completeProcess('ApiKeyCredentialService.create');
            })
        );
    }

    update(id: number, apiKeyCredential: ApiKeyCredential, additionalHeaders: any = {}): Observable<ApiKeyCredential> {
        this.#spinnerService.addProcess('ApiKeyCredentialService.update', 'Updating your api key credential...');
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };

        return this.#http
            .patch<ApiKeyCredential>(this.API_KEY_CREDENTIAL_URL + '/' + id, apiKeyCredential, httpOptions)
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while updating your credential. Please try again later.'
                    );
                    return of<ApiKeyCredential>();
                }),
                finalize(() => {
                    this.#spinnerService.completeProcess('ApiKeyCredentialService.update');
                })
            );
    }

    get(id: number, additionalHeaders: any = {}): Observable<ApiKeyCredential> {
        this.#spinnerService.addProcess('ApiKeyCredentialService.getById.' + id, 'Loading your api key credential...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.get<ApiKeyCredential>(`${this.API_KEY_CREDENTIAL_URL}/${id}`, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving api key credential data from Relationship One. Please try again later.'
                );
                return of<ApiKeyCredential>();
            }),
            finalize(() => this.#spinnerService.completeProcess('ApiKeyCredentialService.getById.' + id))
        );
    }

    delete(id: number, additionalHeaders: any = {}): Observable<any> {
        this.#spinnerService.addProcess('ApiKeyCredentialService.delete.' + id, 'Deleting your api key credential...');

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.delete<Observable<any>>(`${this.API_KEY_CREDENTIAL_URL}/${id}`, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while deleting your api key credential. Try again later.'
                );
                return of<Observable<any>>();
            }),
            finalize(() => this.#spinnerService.completeProcess('ApiKeyCredentialService.delete.' + id))
        );
    }
}
